import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { invoiceSortFields, type InvoiceListQuery } from '@faktorplus/shared';
import { fetchAllInvoices, useInvoiceList, useRestoreInvoice } from '../../api/invoices';
import { InvoiceStateBadge, invoiceRowTone } from '../../components/ui/status-pill';
import { Button } from '../../components/ui/button';
import { EmptyState, ErrorBox, LoadingBlock } from '../../components/ui/feedback';
import { ApiError } from '../../lib/api-client';
import { useCurrentUser } from '../../auth/auth-provider';
import { StatTile, StatTiles } from '../../components/ui/stat-tile';
import { CoinsIcon, FileIcon, PercentIcon, WalletIcon } from '../../components/ui/icons';
import {
  formatAmount,
  formatDayCount,
  formatMoney,
  formatMoneyCompact,
  formatShortDate,
} from '../../lib/format';
import { exportInvoicesToCsv } from './export-invoices';
import { managerLabel, projectLabel } from './invoice-labels';
import { CreateInvoiceDialog } from './create-invoice-dialog';
import { hasAnyInvoiceFilter, InvoiceFilters, readInvoiceFilters } from './invoice-filters';
import { SortableTableHeader } from '../../components/ui/sortable-table-header';
import styles from '../../components/layout/page.module.css';

const ROW_TONE_CLASS = {
  late: styles.rowLate,
  cash: styles.rowCash,
  none: undefined,
} as const;

const PAGE_SIZE = 40;

/**
 * The register. Deliberately reads like a very good spreadsheet, because that
 * is what it replaces — same columns, same filters, plus the things Excel could
 * not do: real statuses, a hard "missing protocol" flag and one dictionary per
 * project instead of three spellings of the same site.
 *
 * Filters live in the URL so that opening an invoice and coming back does not
 * throw them away, and so a filtered view can be sent to someone.
 */
export function InvoiceRegisterPage() {
  const navigate = useNavigate();
  const user = useCurrentUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isCreating, setIsCreating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const restore = useRestoreInvoice();

  const filters = readFilters(searchParams);
  const { data, isLoading, error } = useInvoiceList({ ...filters, pageSize: PAGE_SIZE });

  const isOffice = user.role === 'ADMIN' || user.role === 'ACCOUNTANT';
  // The trash. A separate view rather than a filter: deleted and live invoices
  // never belong on one list, and the office decided they must be restorable
  // (24.09.2026) — so they need somewhere to be seen.
  const isTrash = filters.deleted === true;
  const canAddInvoice = isOffice && !isTrash;

  function update(patch: Record<string, string>) {
    const next = new URLSearchParams(searchParams);

    for (const [key, value] of Object.entries(patch)) {
      if (value) next.set(key, value);
      else next.delete(key);
    }
    // Any filter change invalidates the current page number.
    if (!('page' in patch)) next.delete('page');

    setSearchParams(next, { replace: true });
  }

  async function handleExport() {
    setIsExporting(true);
    try {
      exportInvoicesToCsv((await fetchAllInvoices(filters)).items);
    } finally {
      setIsExporting(false);
    }
  }

  const invoices = data?.items ?? [];
  const total = data?.total ?? 0;
  const page = filters.page ?? 1;
  const sortBy = filters.sortBy ?? 'receivedDate';
  const sortDir = filters.sortDir ?? 'desc';
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  function sort(column: string) {
    const nextSortBy = column as InvoiceListQuery['sortBy'];
    const nextSortDir = sortBy === nextSortBy && sortDir === 'asc' ? 'desc' : 'asc';
    update({ sortBy: nextSortBy, sortDir: nextSortDir });
  }

  return (
    <>
      <header className={styles.header}>
        <h1 className={styles.title}>{isTrash ? 'Usunięte' : 'Rejestr faktur'}</h1>
        <div className={styles.actions}>
          {isOffice && (
            <Button
              onClick={() =>
                setSearchParams(isTrash ? {} : { deleted: 'true' }, { replace: true })
              }
            >
              {isTrash ? '← Wróć do rejestru' : 'Usunięte'}
            </Button>
          )}
          <Button onClick={() => void handleExport()} isLoading={isExporting} disabled={total === 0}>
            ↓ Eksport CSV ({total})
          </Button>
          {canAddInvoice && (
            <Button variant="primary" onClick={() => setIsCreating(true)}>
              + Dodaj fakturę
            </Button>
          )}
        </div>
      </header>

      {data?.summary && total > 0 && (
        <StatTiles>
          <StatTile
            icon={<FileIcon />}
            label="Faktur w wynikach"
            value={String(data.summary.invoiceCount)}
          />
          <StatTile
            icon={<CoinsIcon />}
            label="Netto"
            value={formatMoneyCompact(data.summary.netAmount)}
            note={formatMoney(data.summary.netAmount)}
          />
          <StatTile
            icon={<PercentIcon />}
            label="VAT"
            value={formatMoneyCompact(data.summary.vatAmount)}
            note={formatMoney(data.summary.vatAmount)}
          />
          <StatTile
            tone="accent"
            icon={<WalletIcon />}
            label="Brutto"
            value={formatMoneyCompact(data.summary.grossAmount)}
            note={formatMoney(data.summary.grossAmount)}
          />
        </StatTiles>
      )}

      <InvoiceFilters
        params={searchParams}
        onChange={update}
        onReset={() => setSearchParams(isTrash ? { deleted: 'true' } : {}, { replace: true })}
      />

      {error && <ErrorBox>Nie udało się pobrać rejestru.</ErrorBox>}
      {restore.error && (
        <ErrorBox>
          {restore.error instanceof ApiError
            ? restore.error.message
            : 'Nie udało się przywrócić faktury.'}
        </ErrorBox>
      )}
      {isLoading && <LoadingBlock />}

      {!isLoading && invoices.length === 0 && (
        <EmptyState
          mark="∅"
          title={isTrash ? 'Nic nie zostało usunięte' : 'Brak faktur'}
          description={
            isTrash
              ? 'Usunięte faktury trafiają tutaj. Każdą można przywrócić.'
              : hasAnyInvoiceFilter(searchParams)
                ? 'Nic nie pasuje do filtrów. Zmień je albo wyczyść.'
                : 'W rejestrze nie ma jeszcze żadnych faktur.'
          }
        />
      )}

      {invoices.length > 0 && (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <SortableTableHeader label="Numer" column="number" activeColumn={sortBy} direction={sortDir} onSort={sort} />
                <th className={styles.hideOnPhone}>Poz. ks.</th>
                <SortableTableHeader label="Data" column="issueDate" activeColumn={sortBy} direction={sortDir} onSort={sort} className={styles.hideOnPhone} />
                <SortableTableHeader label="Kontrahent" column="vendorName" activeColumn={sortBy} direction={sortDir} onSort={sort} />
                <th>Budowa</th>
                <th className={styles.hideOnPhone}>Kierownik</th>
                <th className={styles.hideOnPhone}>Kategoria</th>
                <SortableTableHeader label="Netto" column="netAmount" activeColumn={sortBy} direction={sortDir} onSort={sort} className={`${styles.numeric} ${styles.hideOnPhone}`} />
                <SortableTableHeader label="Brutto" column="grossAmount" activeColumn={sortBy} direction={sortDir} onSort={sort} className={styles.numeric} />
                <SortableTableHeader label="Data zapłaty" column="paidAt" activeColumn={sortBy} direction={sortDir} onSort={sort} className={styles.hideOnPhone} />
                <SortableTableHeader label="Status" column="status" activeColumn={sortBy} direction={sortDir} onSort={sort} />
                {isTrash && <th />}
              </tr>
            </thead>
            <tbody>
              {invoices.map((invoice) => {
                const project = projectLabel(invoice);
                const waiting = invoice.daysAwaitingManager;

                return (
                  <tr
                    key={invoice.id}
                    onClick={() => void navigate(`/faktury/${invoice.id}`)}
                    className={ROW_TONE_CLASS[invoiceRowTone(invoice) ?? 'none']}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className={styles.docNumber}>{invoice.number}</td>
                    <td className={`${styles.muted} ${styles.hideOnPhone}`}>
                      {invoice.ledgerRef ?? '—'}
                    </td>
                    <td className={`${styles.muted} ${styles.hideOnPhone}`}>
                      {formatShortDate(invoice.issueDate)}
                    </td>
                    <td className={styles.strong}>{invoice.vendorName}</td>
                    <td className={project.isPlaceholder ? styles.muted : undefined}>
                      {project.text}
                    </td>
                    <td className={styles.hideOnPhone}>
                      {managerLabel(invoice)}
                      {waiting !== null && waiting > 0 && (
                        <span className={styles.waitingNote}> · {formatDayCount(waiting)}</span>
                      )}
                    </td>
                    <td className={`${styles.muted} ${styles.hideOnPhone}`}>
                      {invoice.categoryName ?? '—'}
                    </td>
                    <td className={`${styles.numeric} ${styles.hideOnPhone}`}>
                      {formatAmount(invoice.netAmount)}
                    </td>
                    <td className={`${styles.numeric} ${styles.strong}`}>
                      {formatAmount(invoice.grossAmount)}
                    </td>
                    <td className={`${styles.muted} ${styles.hideOnPhone}`}>
                      {formatShortDate(invoice.paidAt)}
                    </td>
                    <td>
                      <InvoiceStateBadge invoice={invoice} />
                    </td>
                    {isTrash && (
                      <td>
                        <Button
                          isLoading={restore.isPending && restore.variables === invoice.id}
                          onClick={(event) => {
                            event.stopPropagation();
                            restore.mutate(invoice.id);
                          }}
                        >
                          Przywróć
                        </Button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {lastPage > 1 && (
        <div className={styles.pagination}>
          <Button disabled={page <= 1} onClick={() => update({ page: String(page - 1) })}>
            ← Poprzednia
          </Button>
          <span>
            Strona {page} z {lastPage}
          </span>
          <span className={styles.paginationSpacer} />
          <Button disabled={page >= lastPage} onClick={() => update({ page: String(page + 1) })}>
            Następna →
          </Button>
        </div>
      )}

      {isCreating && <CreateInvoiceDialog onClose={() => setIsCreating(false)} />}
    </>
  );
}

function readFilters(params: URLSearchParams): Partial<InvoiceListQuery> {
  const sortBy = params.get('sortBy');
  const sortDir = params.get('sortDir');

  return {
    ...readInvoiceFilters(params),
    // Read here rather than in `readInvoiceFilters`: the trash is this page's
    // view, not a filter payables or project lists should ever inherit.
    ...(params.get('deleted') === 'true' ? { deleted: true } : {}),
    page: Number(params.get('page')) || 1,
    sortBy: invoiceSortFields.find((candidate) => candidate === sortBy),
    sortDir: sortDir === 'asc' || sortDir === 'desc' ? sortDir : undefined,
  };
}
